import { useEffect, useRef, useState } from "react";
import { publicApi } from "./lib/api";

type TurnstileAction = "auth_access" | "account_claim";
type TurnstileConfig = {
  required: boolean;
  site_key: string | null;
  mode: "managed";
};

type TurnstileApi = {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: TurnstileAction;
      theme: "auto";
      callback(token: string): void;
      "expired-callback"(): void;
      "error-callback"(): void;
    },
  ): string;
  remove(widgetId: string): void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loader: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (loader) return loader;

  loader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-wordwill-turnstile]");
    const ready = () => window.turnstile ? resolve() : reject(new Error("turnstile_unavailable"));
    if (existing) {
      existing.addEventListener("load", ready, { once: true });
      existing.addEventListener("error", () => reject(new Error("turnstile_load_failed")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.wordwillTurnstile = "true";
    script.addEventListener("load", ready, { once: true });
    script.addEventListener("error", () => reject(new Error("turnstile_load_failed")), { once: true });
    document.head.appendChild(script);
  });
  return loader;
}

export function TurnstileGate({
  action,
  resetNonce,
  onToken,
}: {
  action: TurnstileAction;
  resetNonce: number;
  onToken(token: string): void;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const [message, setMessage] = useState("Checking browser…");

  useEffect(() => {
    let active = true;
    let widgetId: string | null = null;
    onToken("");

    void publicApi<TurnstileConfig>("/api/security/turnstile-config")
      .then(async config => {
        if (!active) return;
        if (!config.required) {
          setMessage("");
          onToken("turnstile_not_required");
          return;
        }
        if (!config.site_key || !container.current) throw new Error("turnstile_not_configured");
        await loadTurnstile();
        if (!active || !container.current || !window.turnstile) return;
        widgetId = window.turnstile.render(container.current, {
          sitekey: config.site_key,
          action,
          theme: "auto",
          callback(token) {
            if (!active) return;
            setMessage("");
            onToken(token);
          },
          "expired-callback"() {
            if (!active) return;
            setMessage("Browser check expired. Refreshing…");
            onToken("");
          },
          "error-callback"() {
            if (!active) return;
            setMessage("Browser check could not complete. Try again.");
            onToken("");
          },
        });
      })
      .catch(() => {
        if (!active) return;
        setMessage("Browser verification is temporarily unavailable.");
        onToken("");
      });

    return () => {
      active = false;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [action, resetNonce, onToken]);

  return <div data-testid="turnstile-gate">
    <div ref={container}/>
    {message && <p className="fine" role="status">{message}</p>}
  </div>;
}
